/** Partidos de alguien dado de baja (Eduardo 2026-10-05, torneo 69 Cat B:
 * «sigue apareciendo Vicky con todo y que la dimos de baja; debería aparecer
 * status cancelado por baja… esa semana para su contrincante sería BYE»).
 *
 * La baja ofrece tres caminos para sus pendientes y cada uno se ve distinto:
 * - «Cancelar»: status `cancelled` con un lado dado de baja o la nota
 *   «Baja de <nombre>» → «Cancelado por baja» (su rival descansa esa
 *   jornada; lo decide el servidor al armar los descansos).
 * - «Dar W/O al rival»: W.O./default/no se presentó con un lado de baja (y
 *   el ganador es el otro) → «W.O. por baja» (victoria del rival, no descanso).
 * - «Dejarlos como están»: sigue pendiente con un lado de baja → «Baja de
 *   Vicky · pendiente», con la acción «Cancelar por baja».
 * El nombre de quien se dio de baja sigue en el renglón (es historial), pero
 * marcado: «Vicky Cattan Michan · baja». */

import type { MatchParticipantBrief, TournamentMatch } from "./api";

export type DropMatchKind = "cancelled" | "walkover" | "pending";

export interface DropMatchState {
  kind: DropMatchKind;
  /** Quién se dio de baja (null si solo lo dice la nota del partido). */
  dropped: MatchParticipantBrief | null;
  /** Nombre corto para el chip («Vicky»); vacío si no se sabe. */
  shortName: string;
}

type MatchLike = Pick<TournamentMatch, "status" | "notes" | "winner_participant_id"> & {
  player1?: MatchParticipantBrief | null;
  player2?: MatchParticipantBrief | null;
};

const PENDING_STATUSES = new Set(["scheduled", "in_progress", "postponed"]);
// Mismos que `esResultadoDeBaja` (lib/jornadaActual): resultados sin jugar.
const WALKOVER_STATUSES = new Set(["walkover", "defaulted", "no_show"]);

/** Sufijo con que se marca el nombre de quien se dio de baja. */
export const DROPPED_NAME_SUFFIX = " · baja";

export function isDroppedParticipant(
  participant: { current_status?: string | null } | null | undefined,
): boolean {
  return participant?.current_status === "dropped";
}

/** «Vicky Cattan Michan» → «Vicky Cattan Michan · baja» si se dio de baja. */
export function nameWithDropMark(
  name: string,
  participant: { current_status?: string | null } | null | undefined,
): string {
  return isDroppedParticipant(participant) ? `${name}${DROPPED_NAME_SUFFIX}` : name;
}

/** Nota con que se reactiva un «Cancelado por baja» (↷ Reactivar en
 * Resultados). Las notas se acumulan: lo que venga después ya no es «por
 * baja» (revisión 2026-10-05). */
export const DROP_REVIVE_NOTE = "Se anuló la cancelación por baja";

/** Nombre que deja la baja en la nota del partido: «Baja de Vicky Cattan
 * Michan[: motivo]» (players.services._drop_match_reason). Cuenta la última
 * baja que no se haya anulado después con DROP_REVIVE_NOTE. */
export function dropNoteName(notes: string | null | undefined): string | null {
  if (!notes) return null;
  let name: string | null = null;
  for (const line of notes.split("\n")) {
    if (line.trim() === DROP_REVIVE_NOTE) {
      name = null;
      continue;
    }
    const found = /^\s*Baja de\s+(.+?)\s*(?::.*)?$/.exec(line);
    if (found && found[1].trim()) name = found[1].trim();
  }
  return name;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

function droppedShortName(participant: MatchParticipantBrief): string {
  // Pareja: su nombre corto («Ana / Bea»); jugador: el primer nombre.
  if (participant.participant_kind === "team" || participant.team_id) {
    return (participant.short_name ?? participant.display_name ?? "").trim();
  }
  return firstName(participant.display_name ?? participant.short_name ?? "");
}

export function dropMatchState(match: MatchLike | null | undefined): DropMatchState | null {
  if (!match) return null;
  const status = String(match.status ?? "");
  const dropped = isDroppedParticipant(match.player1)
    ? match.player1 ?? null
    : isDroppedParticipant(match.player2)
      ? match.player2 ?? null
      : null;
  if (status === "cancelled") {
    const note = dropNoteName(match.notes);
    if (!dropped && note === null) return null;
    return {
      kind: "cancelled",
      dropped,
      shortName: dropped ? droppedShortName(dropped) : firstName(note ?? ""),
    };
  }
  if (!dropped) return null;
  if (WALKOVER_STATUSES.has(status)) {
    // «W.O. por baja» = la victoria es del rival. Un W.O. que ganó quien
    // después se dio de baja, o un «no se presentó» sin ganador, no lo es.
    if (match.winner_participant_id == null || match.winner_participant_id === dropped.id) return null;
    return { kind: "walkover", dropped, shortName: droppedShortName(dropped) };
  }
  if (PENDING_STATUSES.has(status)) {
    return { kind: "pending", dropped, shortName: droppedShortName(dropped) };
  }
  return null;
}

/** Texto del chip: «Cancelado por baja», «W.O. por baja», «Baja de Vicky · pendiente». */
export function dropMatchLabel(state: DropMatchState): string {
  if (state.kind === "cancelled") return "Cancelado por baja";
  if (state.kind === "walkover") return "W.O. por baja";
  return state.shortName ? `Baja de ${state.shortName} · pendiente` : "Baja · pendiente";
}

/** Clase del chip (mismas píldoras `kbd` de los estatus). */
export function dropMatchPillClass(state: DropMatchState): string {
  if (state.kind === "cancelled") return "kbd kbd--drop";
  if (state.kind === "walkover") return "kbd kbd--ok";
  return "kbd kbd--warning";
}

/** Partido cancelado por una baja: no cuenta como «por jugar» ni como
 * «programado» y no lleva controles de captura ni de programación. */
export function isCancelledByDrop(match: MatchLike | null | undefined): boolean {
  return dropMatchState(match)?.kind === "cancelled";
}

/** Pendiente con un lado dado de baja: lleva «Cancelar por baja». */
export function isPendingWithDrop(match: MatchLike | null | undefined): boolean {
  return dropMatchState(match)?.kind === "pending";
}

/** Nota con que se cancela por la baja (la misma que deja la baja). */
export function dropCancelReason(state: DropMatchState): string {
  const name = state.dropped?.display_name?.trim() || state.shortName;
  return name ? `Baja de ${name}` : "Baja";
}
